import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  ReactElement,
  cloneElement
} from "react"
import { useDebouncedCallback } from "../../core/hooks"
import Tooltip, { TooltipSize, TooltipType } from "./Tooltip"
import { TooltipProps } from "@mui/material"

interface EllipsisTooltipProps extends Omit<TooltipProps, "children"> {
  children: ReactElement
  size?: TooltipSize
  type?: TooltipType
}

const EllipsisTooltip: React.FC<EllipsisTooltipProps> = ({
  children,
  title,
  size = "sm",
  type = "label",
  ...rest
}) => {
  const elementRef = useRef<HTMLElement>(null)
  const [hasEllipsis, setHasEllipsis] = useState(false)

  const checkEllipsis = useCallback(() => {
    if (elementRef.current) {
      const element = elementRef.current
      const hasOverflow = element.scrollWidth > element.clientWidth
      setHasEllipsis(hasOverflow)
    }
  }, [])

  const debouncedCheckEllipsis = useDebouncedCallback(checkEllipsis, 150)

  const handleRef = useCallback(
    (node: HTMLElement) => {
      elementRef.current = node
      // Call original ref if it exists
      const originalRef = (children as any).ref
      if (typeof originalRef === "function") {
        originalRef(node)
      } else if (originalRef) {
        originalRef.current = node
      }
    },
    [children]
  )

  useEffect(() => {
    // Use requestAnimationFrame to ensure DOM is fully rendered
    const rafId = requestAnimationFrame(() => {
      checkEllipsis()
    })

    // Re-check on window resize with debounce
    window.addEventListener("resize", debouncedCheckEllipsis)
    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener("resize", debouncedCheckEllipsis)
    }
  }, [children, title, checkEllipsis, debouncedCheckEllipsis])

  // Clone the child element and add ref
  const childWithRef = cloneElement(children, {
    ref: handleRef
  })

  return (
    <Tooltip
      title={title}
      size={size}
      type={type}
      showTooltip={hasEllipsis}
      {...rest}
    >
      {childWithRef}
    </Tooltip>
  )
}

export default EllipsisTooltip
